import React from 'react';
import moment from 'moment';
import classes from './ImageModerationContainer.module.css';
import {
  ImageScanHistoryResponse,
  ImageScanRow,
  categoryLabel
} from './types';

/**
 * Why one photo was flagged, and who has acted on it. Shows every scan row, not
 * just the deciding one — provider disagreement is the false-positive signal.
 */

const formatTimestamp = (value: string | null) =>
  value ? moment(value).format('MMM D, YYYY h:mm A') : null;

const ScanRowCard = ({
  scan,
  autoHide,
  questionable,
  neverAutoHide
}: {
  scan: ImageScanRow;
  autoHide: number;
  questionable: number;
  neverAutoHide: string[];
}) => {
  // At or above the review floor only, strongest first.
  const scored = Object.entries(scan.categories)
    .filter(
      ([, cat]) =>
        typeof cat.confidence === 'number' && cat.confidence >= questionable
    )
    .sort((a, b) => (b[1].confidence ?? 0) - (a[1].confidence ?? 0));

  return (
    <div
      className={`${classes.scanRow} ${
        scan.isPrimary ? classes.scanRowPrimary : ''
      }`}
    >
      <div className={classes.scanHeader}>
        <span>
          {scan.provider} / {scan.model}
        </span>
        {scan.isPrimary ? (
          <span className={classes.primaryBadge}>DECIDED</span>
        ) : (
          <span className={classes.shadowBadge}>COMPARISON ONLY</span>
        )}
        <span className={classes.mutedText}>
          verdict: {scan.state ?? 'none'} · attempt {scan.attempt}
          {scan.promptVersion ? ` · prompt ${scan.promptVersion}` : ''}
          {typeof scan.latencyMs === 'number' ? ` · ${scan.latencyMs}ms` : ''}
        </span>
      </div>

      {scan.summary && (
        <p style={{ marginBottom: 8, fontStyle: 'italic' }}>{scan.summary}</p>
      )}

      {scored.length > 0 ? (
        <div className={classes.categoryGrid}>
          {scored.map(([key, cat]) => {
            const confidence = cat.confidence as number;
            const withheldBy =
              confidence >= autoHide && !neverAutoHide.includes(key);

            return (
              <React.Fragment key={key}>
                <span>
                  {categoryLabel(key)}
                  {cat.reason ? ` — ${cat.reason}` : ''}
                  {confidence >= autoHide && neverAutoHide.includes(key) && (
                    <span className={classes.mutedBadge}> never auto-hides</span>
                  )}
                </span>
                <span
                  className={withheldBy ? classes.redText : classes.amberText}
                >
                  {Math.round(confidence)}%
                </span>
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        <p className={classes.mutedText}>
          Nothing scored at or above {questionable}%.
        </p>
      )}

      {scan.error && (
        <div className={classes.errorBox}>
          <strong>{scan.error.code || 'Error'}:</strong>{' '}
          {scan.error.message || 'no detail recorded'}
        </div>
      )}
    </div>
  );
};

const ScanDetailModal = ({ data }: { data?: ImageScanHistoryResponse }) => {
  if (!data) return null;

  const { image, scans, thresholds, neverAutoHide } = data;

  const distinctVerdicts = Array.from(
    new Set(scans.filter((s) => s.state).map((s) => s.state))
  );
  const hasDisagreement = distinctVerdicts.length > 1;

  return (
    <div style={{ padding: '8px 0' }}>
      <img src={image.image} alt='Moderated upload' className={classes.detailImage} />

      <div className={classes.sectionTitle}>Staff action history</div>
      <div className={classes.auditRow}>
        <span className={classes.auditLabel}>Current state</span>
        <span>
          {image.scanVerdict} · {image.hidden ? 'withheld' : 'visible'}
        </span>
      </div>
      <div className={classes.auditRow}>
        <span className={classes.auditLabel}>Hidden by</span>
        <span>
          {image.hiddenByName || '—'}
          {formatTimestamp(image.hiddenAt)
            ? ` · ${formatTimestamp(image.hiddenAt)}`
            : ''}
        </span>
      </div>
      <div className={classes.auditRow}>
        <span className={classes.auditLabel}>Reviewed by</span>
        <span>
          {image.moderatedByName || 'not yet reviewed'}
          {formatTimestamp(image.moderatedAt)
            ? ` · ${formatTimestamp(image.moderatedAt)}`
            : ''}
        </span>
      </div>

      <div className={classes.sectionTitle}>
        Scan results ({scans.length})
      </div>

      {hasDisagreement && (
        <div className={classes.disagreementNotice}>
          The engines disagreed ({distinctVerdicts.join(' vs ')}). Worth a closer
          look — disagreement is the main signal that an automatic hide was wrong.
        </div>
      )}

      {scans.length === 0 ? (
        <p className={classes.mutedText}>
          No scan was ever recorded for this image.
        </p>
      ) : (
        scans.map((scan) => (
          <ScanRowCard
            key={scan.uuid}
            scan={scan}
            autoHide={thresholds.autoHide}
            questionable={thresholds.questionable}
            neverAutoHide={neverAutoHide}
          />
        ))
      )}
    </div>
  );
};

export default ScanDetailModal;
